import React, { useState, useEffect, useCallback } from 'react';
import { CREATE_SONG_URL, GET_SONGS_URL } from '../../api/song';
import { apiClient } from '../../api/apiClient';
import { usePermissions } from '../../utils/usePermissions';
import ProtectedPage from '../../components/shared/ProtectedPage';
import SharedHeader from '../../components/shared/SharedHeader';
import SongTable from './components/SongTable';
import SortPopup from './components/SortPopup';
import FilterPopup from './components/FilterPopup';
import AddSong from './components/AddSong';
import LoadingOverlay from '../../components/shared/LoadingOverlay';
import { toast } from '../../components/shared/toast';
import en from '../../translations/en.json';
import './Songs.css';
import { filterSongsByOwnership } from './utils/ownershipFilter';
import { hasConflict } from '../../utils/songDataFormatters';

const normalizeSongConflict = (song) => {
  const conflictPayload = {
    conflicts: song?.conflicts ?? song?.songConflicts ?? song?.song_conflicts ?? song?.conflictRecords ?? song?.conflict_records,
    conflictCount: song?.conflictCount ?? song?.conflictsCount,
    activeConflictCount: song?.activeConflictCount,
    conflict: song?.conflict,
  };

  const conflictBoolean = hasConflict(conflictPayload);

  return {
    ...song,
    conflict: conflictBoolean,
    conflictBoolean,
  };
};

function Songs({ onSelectSong }) {
  const { canCreate } = usePermissions();
  const [songs, setSongs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddingSong, setIsAddingSong] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSort, setSelectedSort] = useState('Songs A-Z');
  const [filters, setFilters] = useState({
    status: null,
    conflict: null,
    ownership: null,
    version: null,
  });
  
  const [currentPage, setCurrentPage] = useState(1);
  const [totalSongs, setTotalSongs] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const songsPerPage = 25;

  const [sortOpen, setSortOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  // Check permissions
  const canCreateSongs = canCreate('songs');

  const t = (key) => {
    return key.split('.').reduce((obj, i) => (obj ? obj[i] : null), en) || key;
  };

  const getStatusValue = (status) => {
    if (!status) return '';
    return status.toLowerCase() === 'active' ? '1' : '0';
  };

  const getConflictValue = (conflict) => {
    if (!conflict) return '';
    return conflict.toLowerCase() === 'yes' ? '1' : '0';
  };

  const fetchSongs = useCallback(async () => {
    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: songsPerPage,
        search: searchQuery,
        status: getStatusValue(filters.status),
        conflict: getConflictValue(filters.conflict),
        ownership: filters.ownership || '',
        versionType: filters.version || '',
        sort: selectedSort,
      }).toString();

      const data = await apiClient(`${GET_SONGS_URL}?${queryParams}`);
      if (data) {
        const fetchedSongs = data.songs || [];
        const normalizedSongs = fetchedSongs.map(normalizeSongConflict);
        const filteredSongs = filterSongsByOwnership(normalizedSongs, filters.ownership);
        setSongs(filteredSongs);
        setTotalSongs(data.totalCount || data.totalRecords || 0);
      }
    } catch (err) {
      console.warn('Failed to load songs from backend:', err);
      setSongs([]);
      setTotalSongs(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchQuery, filters.status, filters.conflict, filters.ownership, filters.version, selectedSort]);

  useEffect(() => {
    fetchSongs();
  }, [currentPage, searchQuery, filters.status, filters.conflict, filters.ownership, filters.version, selectedSort]);

  // Dynamic filter change
  const handleFilterChange = (field, value) => {
    setFilters((current) => ({
      ...current,
      [field]: value,
    }));
    setCurrentPage(1); // Reset to page 1 on filter
  };

  // Reset all filters
  const handleResetFilters = () => {
    setFilters({
      status: null,
      conflict: null,
      ownership: null,
      version: null,
    });
    setSearchQuery('');
    setCurrentPage(1);
  };

  const downloadCSV = (songsList) => {
    const headers = ['Song Name', 'Sinhala Name', 'Status', 'Artist', 'Lyrics', 'Music', 'Ownership %', 'Notes', 'Conflicts', 'Version Type', 'Distributor'];
    const rows = songsList.map(s => [
      s.name || '',
      s.nameSinhala || '',
      s.status || '',
      s.artist || '',
      s.lyrics || '',
      s.music || '',
      s.ownership || 100,
      s.notes || '',
      s.conflict || '',
      s.versionType || '',
      s.distributionProvider || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `songs_export_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      await new Promise(resolve => setTimeout(resolve, 800)); // export animation delay matching Artist tab

      const queryParams = new URLSearchParams({
        search: searchQuery,
        status: filters.status || '',
        conflict: filters.conflict || '',
        ownership: filters.ownership || '',
        versionType: filters.version || '',
        sort: selectedSort,
        export: 'true'
      }).toString();

      const data = await apiClient(`${GET_SONGS_URL}?${queryParams}`);
      if (data && Array.isArray(data)) {
        downloadCSV(data.map(normalizeSongConflict));
        toast.success('Data exported successfully!');
      } else {
        toast.error('Failed to export data.');
      }
    } catch (err) {
      console.error('Export Error:', err);
      toast.error('Error exporting data.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleSaveSong = async (newSongData) => {
    try {
      const formData = new FormData();
      formData.append('name', newSongData.songName);
      formData.append('nameSinhala', newSongData.songNameSinhala);
      
      if (newSongData.track && newSongData.track.file) {
        formData.append('track', newSongData.track.file);
      }
      if (newSongData.art && newSongData.art.file) {
        formData.append('art', newSongData.art.file);
      }
      
      formData.append('versionType', newSongData.originalOrVersion);
      formData.append('isrcCode', newSongData.isrcCode || '');
      formData.append('other', newSongData.other || '');

      if (Array.isArray(newSongData.artists)) {
        newSongData.artists.forEach((id) => formData.append('artists', id));
      }
      if (Array.isArray(newSongData.lyrics)) {
        newSongData.lyrics.forEach((id) => formData.append('lyrics', id));
      }
      if (Array.isArray(newSongData.music)) {
        newSongData.music.forEach((id) => formData.append('music', id));
      }

      // Main artist IDs for revenue distribution per role
      if (newSongData.mainSinger) {
        formData.append('mainSinger', newSongData.mainSinger);
      }
      if (newSongData.mainLyricist) {
        formData.append('mainLyricist', newSongData.mainLyricist);
      }
      if (newSongData.mainMusician) {
        formData.append('mainMusician', newSongData.mainMusician);
      }

      if (newSongData.versionDetails) {
        formData.append('versionDetails', JSON.stringify(newSongData.versionDetails));
      }
      if (newSongData.distribution) {
        formData.append('distribution', JSON.stringify(newSongData.distribution));
      }
      if (newSongData.ringtones) {
        formData.append('ringtones', JSON.stringify(newSongData.ringtones));
      }

      const savedSong = await apiClient(CREATE_SONG_URL, {
        method: 'POST',
        body: formData,
      });

      setSongs((prev) => [savedSong, ...prev]);
      setCurrentPage(1);
      setIsAddingSong(false);
      toast.success('Song saved successfully!');
    } catch (err) {
      console.error('Failed to save song:', err);
      toast.error(err.message || 'Failed to save song.');
    }
  };

  if (isAddingSong) {
    return (
      <AddSong 
        onCancel={() => setIsAddingSong(false)}
        onSave={handleSaveSong}
      />
    );
  }

  return (
    <ProtectedPage module="songs">
      <div className="songs-page-wrapper">
      {/* Header section containing controls */}
      <SharedHeader
        title={t('songs.title')}
        searchQuery={searchQuery}
        onSearchChange={(val) => {
          setSearchQuery(val);
          setCurrentPage(1);
        }}
        resultCount={totalSongs}
        resultLabel={t('songs.resultsCount')}
        onSortClick={() => {
          setSortOpen(!sortOpen);
          setFilterOpen(false);
        }}
        onFilterClick={() => {
          setFilterOpen(!filterOpen);
          setSortOpen(false);
        }}
        sortActive={sortOpen}
        filterActive={filterOpen}
        isSortApplied={selectedSort !== 'Songs A-Z'}
        isFilterApplied={filters.status !== null || filters.conflict !== null || filters.ownership !== null || filters.version !== null}
        onExportClick={handleExport}
        onAddNewClick={() => setIsAddingSong(true)}
        addNewText={t('songs.addNew')}
        canAddNew={canCreateSongs}
      />

      {/* Popups rendered conditionally relative to header buttons */}
      <div className="popup-anchor-container">
        {sortOpen && (
          <SortPopup
            selectedSort={selectedSort}
            onSelect={setSelectedSort}
            onClose={() => setSortOpen(false)}
            t={t}
          />
        )}
        {filterOpen && (
          <FilterPopup
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={handleResetFilters}
            onClose={() => setFilterOpen(false)}
          />
        )}
      </div>

      {/* Songs Grid Table */}
      <SongTable
        songs={songs}
        currentPage={currentPage}
        totalSongs={totalSongs}
        songsPerPage={songsPerPage}
        onPageChange={setCurrentPage}
        onSelectSong={onSelectSong}
        isLoading={isLoading}
        t={t}
      />

      {/* Loading overlay animation for Export */}
      <LoadingOverlay isLoading={isExporting} message={t('common.exportingMessage')} />
      </div>
    </ProtectedPage>
  );
}

export default Songs;
